import { useRef, useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { Reveal } from "@/components/Reveal";
import { Marquee } from "@/components/Marquee";
import { AmbientVideo } from "@/components/AmbientVideo";
import { img, IMG, ASSETS, COLLECTION_LIST, PROJECTS, MANIFESTO } from "@/lib/site";

const MID_HERO_HEADLINE = "Purveyors of Fine Interiors";

const HERO_SLIDES = [
  { src: "/assets/home/kitchen-marble.jpg", alt: "Timber kitchen with Calacatta Viola marble splashback by House of David" },
  { src: "/assets/home/landing-hero.jpg", alt: "Fluted oak kitchen island with natural stone flooring by House of David" },
  { src: "/assets/hero-kitchen.png", alt: "Fluted oak kitchen island with natural stone benchtop" },
  { src: "/assets/hero-patio.png", alt: "Travertine paved patio with timber shutters" },
];

const Hero = () => {
  const [index, setIndex] = useState(0);

  useEffect(() => {
    const id = setInterval(() => setIndex((i) => (i + 1) % HERO_SLIDES.length), 5500);
    return () => clearInterval(id);
  }, []);

  return (
    <section className="relative h-[100svh] overflow-hidden" data-testid="home-hero">
      {HERO_SLIDES.map((s, i) => (
        <motion.div
          key={s.src}
          data-testid={`hero-slide-${i}`}
          className="absolute inset-0"
          initial={false}
          animate={{ opacity: i === index ? 1 : 0 }}
          transition={{ duration: 1.2, ease: [0.22, 1, 0.36, 1] }}
        >
          <motion.img
            src={img(s.src, 2400)}
            alt={s.alt}
            className="h-full w-full object-cover"
            initial={{ scale: 1.08 }}
            animate={{ scale: i === index ? 1 : 1.08 }}
            transition={{ duration: 6, ease: "linear" }}
          />
        </motion.div>
      ))}
      <div className="absolute inset-x-0 bottom-0 h-40 bg-gradient-to-t from-black/25 to-transparent" />

      <div className="relative z-10 flex h-full flex-col justify-end pb-14 edge text-white">
        <div className="flex items-center gap-3" data-testid="hero-dots">
          {HERO_SLIDES.map((s, i) => (
            <button
              key={s.src}
              type="button"
              onClick={() => setIndex(i)}
              data-testid={`hero-dot-${i}`}
              aria-label={`Show slide ${i + 1}`}
              className={`h-[3px] transition-all duration-500 ${
                i === index ? "w-10 bg-white" : "w-5 bg-white/40 hover:bg-white/70"
              }`}
            />
          ))}
        </div>
      </div>
    </section>
  );
};

const Intro = () => (
  <section className="edge py-24 md:py-36" data-testid="home-intro">
    <div className="grid grid-cols-1 gap-10 md:grid-cols-12">
      <div className="md:col-span-9 md:col-start-4">
        <Reveal>
          <p className="text-2xl font-light leading-snug tracking-editorial md:text-4xl">
            We supply architects and designers with an exceptional range of
            timber flooring, wall panels, veneers, natural stone and custom made
            carpet — materials chosen for their character, longevity and quiet
            confidence.
          </p>
        </Reveal>
      </div>
    </div>
  </section>
);

const Categories = () => {
  const cats = COLLECTION_LIST.slice(0, 4);
  return (
    <section className="edge py-24 md:py-36" data-testid="home-categories">
      <div className="mb-14 md:mb-20">
        <span className="eyebrow">Collections</span>
        <h2 className="mt-4 max-w-2xl text-3xl font-light leading-tight tracking-editorial md:text-4xl">
          Explore our collections.
        </h2>
      </div>
      <div className="grid grid-cols-1 gap-x-10 gap-y-16 sm:grid-cols-2 md:gap-x-8 lg:grid-cols-4">
        {cats.map((c) => (
          <Link
            key={c.slug}
            to={`/collections/${c.slug}`}
            data-testid={`category-tile-${c.slug}`}
            className="group block"
          >
            <div className="aspect-[4/5] overflow-hidden bg-secondary/40">
              <img
                src={img(c.homeImage || c.banner, 1200)}
                alt={c.name}
                className="h-full w-full object-cover transition-transform duration-[1200ms] ease-[cubic-bezier(0.22,1,0.36,1)] group-hover:scale-105"
              />
            </div>
            <div className="mt-6 flex items-baseline justify-between">
              <div>
                <span className="eyebrow block">{c.index}</span>
                <h3 className="mt-3 text-2xl font-light tracking-editorial">{c.name}</h3>
                <p className="mt-2 text-sm text-muted-foreground">{c.tagline}</p>
              </div>
              <span className="text-lg transition-transform duration-500 group-hover:translate-x-1">
                →
              </span>
            </div>
          </Link>
        ))}
      </div>
    </section>
  );
};

const FeaturedProjects = () => {
  const featured = PROJECTS.slice(0, 4);
  return (
    <section className="edge py-24 md:py-36" data-testid="home-projects">
      <div className="mb-14 flex flex-col gap-6 md:flex-row md:items-end md:justify-between">
        <div>
          <span className="eyebrow">Selected Work</span>
          <h2 className="mt-4 text-4xl font-light tracking-editorial md:text-6xl">
            Recent projects
          </h2>
        </div>
        <Link
          to="/portfolio"
          data-testid="home-projects-viewall"
          className="text-sm uppercase tracking-[0.16em] underline-offset-4 hover:underline"
        >
          View all →
        </Link>
      </div>

      <div className="grid grid-cols-1 gap-x-8 gap-y-16 md:grid-cols-2">
        {featured.map((p, i) => (
          <Reveal key={p.slug} delay={(i % 2) * 0.1} className={i % 2 ? "md:mt-24" : ""}>
            <Link to={`/portfolio/${p.slug}`} data-testid={`home-project-${p.slug}`} className="group block">
              <div className="overflow-hidden">
                <img
                  src={img(p.cover, 1400)}
                  alt={p.title}
                  className="aspect-[4/5] w-full object-cover transition-transform duration-[1200ms] ease-[cubic-bezier(0.22,1,0.36,1)] group-hover:scale-105"
                />
              </div>
              <div className="mt-5 flex items-baseline justify-between hairline pt-4">
                <h3 className="text-xl font-normal tracking-editorial">{p.title}</h3>
                <span className="eyebrow">{p.category} · {p.year}</span>
              </div>
            </Link>
          </Reveal>
        ))}
      </div>
    </section>
  );
};

const Manifesto = () => (
  <section className="hairline bg-secondary/40" data-testid="home-manifesto">
    <div className="edge grid grid-cols-1 gap-16 py-24 md:grid-cols-12 md:py-32">
      <div className="md:col-span-4">
        <span className="eyebrow">Our Philosophy</span>
        <h2 className="mt-6 text-4xl font-light leading-tight tracking-editorial md:text-5xl">
          A manifesto for making.
        </h2>
        <p className="mt-6 max-w-sm text-sm leading-relaxed text-muted-foreground">
          Everything we bring in is measured against a simple standard: would we
          live with it for a lifetime? These are the principles that guide what
          earns a place in our collection.
        </p>
      </div>
      <div className="md:col-span-7 md:col-start-6">
        <div className="divide-y hairline hairline">
          {MANIFESTO.map((m, i) => (
            <Reveal key={m.no} delay={i * 0.22}>
              <div className="grid grid-cols-12 gap-4 py-8">
                <span className="col-span-2 eyebrow pt-1">{m.no}</span>
                <h3 className="col-span-10 text-2xl font-light tracking-editorial md:col-span-4">
                  {m.title}
                </h3>
                <p className="col-span-12 text-sm leading-relaxed text-muted-foreground md:col-span-6">
                  {m.body}
                </p>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </div>
  </section>
);

const BrandStatement = () => (
  <section className="relative overflow-hidden hairline" data-testid="home-statement">
    <img
      src={img(IMG.beamsShadow, 2400)}
      alt="House of David materials"
      className="absolute inset-0 h-full w-full object-cover"
    />
    <div className="absolute inset-0 bg-black/55" />
    <div className="relative z-10 edge flex min-h-[85vh] flex-col justify-center py-24 text-white md:py-32">
      <div className="max-w-3xl">
        <span className="text-[0.7rem] font-semibold uppercase tracking-[0.22em] text-white/80">
          The Studio
        </span>
        <p className="mt-6 text-2xl font-light leading-relaxed tracking-editorial md:text-4xl">
          Founded on a love of natural materials, House of David is becoming one of
          New Zealand's trusted names in fine interior surfaces — sourcing, curating and
          supplying the timber, stone and finishes that give a space its soul.
        </p>
        <Link
          to="/story"
          data-testid="home-about-link"
          className="mt-10 inline-block w-fit border-b border-white pb-1 text-sm uppercase tracking-[0.16em] text-white transition-opacity hover:opacity-70"
        >
          Read our story →
        </Link>
      </div>
    </div>
  </section>
);

const MidHero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start end", "end start"],
  });
  const y = useTransform(scrollYProgress, [0, 1], ["-12%", "12%"]);

  return (
    <section
      ref={ref}
      className="relative h-[60vh] overflow-hidden hairline md:h-[80vh]"
      data-testid="home-midhero"
    >
      <motion.img
        src={img(IMG.midHero, 2400)}
        alt="Feature image placeholder"
        style={{ y, scale: 1.2 }}
        className="absolute inset-0 h-full w-full object-cover"
      />
      <div className="absolute inset-0 bg-black/30" />
      <div className="relative z-10 flex h-full flex-col items-center justify-center px-6 text-center text-white">
        <h2 className="max-w-3xl text-3xl font-light leading-tight tracking-editorial md:text-6xl" data-testid="midhero-headline">
          {MID_HERO_HEADLINE}
        </h2>
      </div>
    </section>
  );
};

const ctas = [
  {
    title: ["Request a Project", "Consultation"],
    action: "Fill out Form",
    to: "/contact",
    img: IMG.consult,
    testid: "cta-consultation",
  },
  {
    title: ["View our", "Collection"],
    action: "View",
    to: "/collection",
    img: "/assets/home/view-collection.jpg",
    testid: "cta-collection",
  },
];

const CTASection = () => (
  <section className="hairline" data-testid="home-cta">
    <div className="grid grid-cols-1 md:grid-cols-2">
      {ctas.map((c, i) => (
        <Link
          key={c.testid}
          to={c.to}
          data-testid={c.testid}
          className={`group flex flex-col border-b hairline p-8 md:p-16 ${
            i === 0 ? "md:border-r hairline" : ""
          }`}
        >
          <h2 className="text-4xl font-light leading-tight tracking-editorial md:text-5xl">
            {c.title.map((line) => (
              <span key={line} className="block">
                {line}
              </span>
            ))}
          </h2>
          <span className="mt-8 inline-flex w-fit items-center gap-3 border-b border-foreground pb-1 text-sm uppercase tracking-[0.16em]">
            {c.action}
            <span className="transition-transform duration-500 group-hover:translate-x-1">→</span>
          </span>
          <div className="mt-10 overflow-hidden">
            <img
              src={img(c.img, 1400)}
              alt={c.title.join(" ")}
              className={`aspect-[16/10] w-full ${c.logo ? "bg-secondary/40 object-contain p-16" : "object-cover transition-transform duration-[1200ms] ease-[cubic-bezier(0.22,1,0.36,1)] group-hover:scale-105"}`}
            />
          </div>
        </Link>
      ))}
    </div>
  </section>
);

const FeaturedProduct = () => (
  <section className="edge py-24 md:py-36" data-testid="home-featured">
    <div className="grid grid-cols-1 items-center gap-12 md:grid-cols-2 md:gap-20">
      <div className="order-2 md:order-1">
        <span className="eyebrow">Featured</span>
        <h2 className="mt-6 text-4xl font-light leading-tight tracking-editorial md:text-5xl">
          Cairo
        </h2>
        <p className="mt-3 text-sm uppercase tracking-[0.16em] text-muted-foreground">
          PrimeOak® Architectural Panel
        </p>
        <p className="mt-8 max-w-md text-base leading-relaxed text-muted-foreground">
          A deep, sculptural scallop milled from solid American Oak — Cairo turns a
          wall into architecture, casting soft, rhythmic shadow across the grain.
        </p>
        <Link
          to="/collections/timber/architectural-panels/cairo"
          data-testid="home-featured-link"
          className="group mt-10 inline-flex w-fit items-center gap-3 border-b border-foreground pb-1 text-sm uppercase tracking-[0.16em]"
        >
          Explore Cairo
          <span className="transition-transform duration-500 group-hover:translate-x-1">→</span>
        </Link>
      </div>
      <div className="order-1 overflow-hidden bg-secondary/30 md:order-2">
        <img
          src={img("/assets/panels/samples/cairo-detail.jpg", 1400)}
          alt="Cairo PrimeOak architectural timber panel"
          className="aspect-[4/5] w-full object-contain"
        />
      </div>
    </div>
  </section>
);

export default function Home() {
  return (
    <>
      <Hero />
      <Intro />
      <AmbientVideo src="/assets/videos/ambient-1.mp4" label="Materials in motion" testid="ambient-video-1" />
      <Categories />
      <Manifesto />
      <AmbientVideo src="/assets/videos/ambient-2.mp4" label="Considered by nature" height="h-[80vh] md:h-[92vh]" testid="ambient-video-2" />
      <CTASection />
      <BrandStatement />
      <FeaturedProduct />
      <section className="w-full overflow-hidden" data-testid="home-bottom-hero">
        <img
          src="/assets/home/bottom-hero.jpg"
          alt="House of David interior in natural materials"
          className="h-[70vh] w-full object-cover md:h-[92vh]"
        />
      </section>
    </>
  );
}
